import React, { useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { CheckCircle, ChevronRight, ChevronLeft, Send, Loader2 } from "lucide-react";

const TELCO_OPTS = [
  { id: "telefonia_movil", label: "Telefonía móvil" },
  { id: "fibra_optica", label: "Fibra óptica" },
  { id: "dispositivos_fichaje", label: "Dispositivos de fichaje" },
  { id: "wifi", label: "WiFi" },
  { id: "videovigilancia", label: "Videovigilancia" },
];
const SW_OPTS = [
  { id: "plataforma_tic", label: "Plataforma TIC" },
  { id: "gis", label: "GIS / Geolocalización" },
  { id: "dotnet", label: "Portal .NET" },
  { id: "foundry", label: "Foundry / Analytics" },
  { id: "gestor_flota", label: "Gestor de flota" },
  { id: "app_ciudadano", label: "App ciudadano" },
  { id: "portal_web", label: "Portal web" },
  { id: "bi_reporting", label: "BI / Reporting" },
  { id: "desarrollo_medida", label: "Desarrollo a medida" },
];
const HW_OPTS = [
  { id: "servidores", label: "Servidores" },
  { id: "rfid", label: "RFID" },
  { id: "gps_flota", label: "GPS flota" },
  { id: "camaras_anpr", label: "Cámaras ANPR" },
  { id: "terminales_moviles", label: "Terminales móviles" },
  { id: "contenedores_smart", label: "Contenedores smart" },
];
const INTEG_OPTS = [
  { id: "api_ayuntamiento", label: "API Ayuntamiento" },
  { id: "erp_corporativo", label: "ERP Corporativo" },
  { id: "sistemas_municipales", label: "Sistemas municipales" },
  { id: "valoriza_park", label: "Valoriza Park" },
  { id: "sigeus", label: "SIGeus" },
];
const CONTRACT_TYPES = ["Limpieza", "Seguridad", "Control de Accesos", "CCTV", "Movilidad", "Residuos", "GIS", "Otro"];

function CheckGroup({ title, options, state, onChange }) {
  return (
    <div className="mb-5">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map(opt => (
          <label key={opt.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${state[opt.id] ? "border-[#007934] bg-[#f0fdf4] text-[#007934] font-medium" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-300"}`}>
            <input type="checkbox" className="hidden" checked={!!state[opt.id]} onChange={e => onChange(opt.id, e.target.checked)} />
            <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${state[opt.id] ? "bg-[#007934] border-[#007934]" : "border-gray-300"}`}>
              {state[opt.id] && <svg viewBox="0 0 10 8" className="w-2.5 h-2" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
            </span>
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

const INITIAL = {
  solicitante_nombre: "", solicitante_cargo: "", solicitante_email: "",
  titulo: "", municipio: "", tipo_contrato: "", descripcion_libre: "",
  plazo_cliente_meses: "", presupuesto_cliente: "", num_habitantes: "",
  notas_adicionales: "",
  servicios_telco: { telefonia_movil: false, fibra_optica: false, dispositivos_fichaje: false, wifi: false, videovigilancia: false },
  servicios_software: { plataforma_tic: false, gis: false, dotnet: false, foundry: false, gestor_flota: false, app_ciudadano: false, portal_web: false, bi_reporting: false, desarrollo_medida: false },
  servicios_hardware: { servidores: false, rfid: false, gps_flota: false, camaras_anpr: false, terminales_moviles: false, contenedores_smart: false },
  servicios_integraciones: { api_ayuntamiento: false, erp_corporativo: false, sistemas_municipales: false, valoriza_park: false, sigeus: false, otras_apis: "" },
};

export default function PublicItPeticion() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [error, setError] = useState("");

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setServiceField = (group, field, val) => setForm(f => ({ ...f, [group]: { ...f[group], [field]: val } }));

  const countSelected = () => {
    const groups = ["servicios_telco", "servicios_software", "servicios_hardware", "servicios_integraciones"];
    return groups.reduce((acc, g) => acc + Object.values(form[g]).filter(v => v === true).length, 0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        plazo_cliente_meses: form.plazo_cliente_meses ? parseInt(form.plazo_cliente_meses) : null,
        presupuesto_cliente: form.presupuesto_cliente ? parseFloat(form.presupuesto_cliente) : null,
        num_habitantes: form.num_habitantes ? parseInt(form.num_habitantes) : null,
      };
      const res = await axios.post(`${API}/it-peticiones/public`, payload);
      setReferencia(res.data.referencia || "");
      setDone(true);
    } catch (e) {
      setError("No se pudo enviar. Por favor inténtalo de nuevo o contacta con plataformascorporativas@valorizasm.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0fdf4] to-[#e8f5e9] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-[#f0fdf4] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-[#007934]" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">¡Petición enviada!</h2>
          <p className="text-gray-500 text-sm mb-4">Tu petición ha sido registrada correctamente. El equipo de Plataformas Corporativas de Valoriza la revisará y se pondrá en contacto contigo.</p>
          {referencia && (
            <div className="bg-[#f0fdf4] border border-[#86efac] rounded-xl p-4 mb-4">
              <p className="text-xs text-gray-400 mb-1">Tu número de referencia</p>
              <p className="text-2xl font-black text-[#007934] tracking-widest">{referencia}</p>
              <p className="text-xs text-gray-400 mt-1">Guárdalo para hacer seguimiento de tu petición</p>
              <a
                href={`/seguimiento-it?ref=${referencia}`}
                className="mt-2 inline-block text-xs font-semibold text-[#007934] underline hover:text-[#006228]"
              >
                Consultar estado de mi petición →
              </a>
            </div>
          )}
          {form.solicitante_email && (
            <p className="text-xs text-gray-400 mb-5">Hemos enviado una confirmación a <strong>{form.solicitante_email}</strong></p>
          )}
          <div className="bg-gray-50 rounded-xl p-4 text-left text-sm text-gray-600 mb-6">
            <p className="font-semibold text-gray-700 mb-2">{form.titulo}</p>
            <p>Municipio: {form.municipio}</p>
            {form.tipo_contrato && <p>Tipo de servicio: {form.tipo_contrato}</p>}
          </div>
          <button onClick={() => { setDone(false); setStep(1); setForm(INITIAL); }} className="text-[#007934] text-sm font-medium hover:underline">
            Enviar otra petición
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0fdf4] to-[#e8f5e9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-[#007934] rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-black">V</span>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 leading-tight">Valoriza</p>
            <p className="text-[11px] text-gray-400 leading-tight">Plataformas Corporativas</p>
          </div>
          <span className="ml-auto text-xs text-gray-400 hidden sm:block">Solicitud de Servicios IT</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Solicitud de Servicios IT/TIC</h1>
          <p className="text-gray-500 text-sm mt-1">Completa el formulario y nuestro equipo técnico realizará una evaluación personalizada</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${s === step ? "text-[#007934]" : s < step ? "text-[#007934]" : "text-gray-300"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${s === step ? "bg-[#007934] text-white" : s < step ? "bg-[#007934] text-white" : "bg-gray-100 text-gray-400"}`}>
                  {s < step ? "✓" : s}
                </div>
                <span className="text-xs font-medium hidden sm:block">{["Datos básicos", "Servicios", "Confirmar"][s - 1]}</span>
              </div>
              {s < 3 && <div className={`flex-1 h-px max-w-12 ${s < step ? "bg-[#007934]" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-4" data-testid="public-step-1">
              <h2 className="font-bold text-gray-900 mb-4">Datos básicos</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Nombre del solicitante *</label>
                  <input value={form.solicitante_nombre} onChange={e => set("solicitante_nombre", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Nombre completo" data-testid="input-nombre" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Cargo / Departamento</label>
                  <input value={form.solicitante_cargo} onChange={e => set("solicitante_cargo", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Director de Servicio, Responsable IT..." />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Email de contacto</label>
                  <input type="email" value={form.solicitante_email} onChange={e => set("solicitante_email", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="nombre@empresa.com" data-testid="input-email" />
                </div>
              </div>
              <hr className="border-gray-100" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Título de la petición *</label>
                  <input value={form.titulo} onChange={e => set("titulo", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Ej: Digitalización servicio de limpieza" data-testid="input-titulo" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Municipio *</label>
                  <input value={form.municipio} onChange={e => set("municipio", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Ej: Madrid, Sevilla..." data-testid="input-municipio" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Tipo de servicio</label>
                  <select value={form.tipo_contrato} onChange={e => set("tipo_contrato", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="">Seleccionar...</option>
                    {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Plazo deseado (meses)</label>
                  <input type="number" min="1" max="60" value={form.plazo_cliente_meses} onChange={e => set("plazo_cliente_meses", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Ej: 6" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Presupuesto estimado (€)</label>
                  <input type="number" value={form.presupuesto_cliente} onChange={e => set("presupuesto_cliente", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Opcional" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Nº de habitantes</label>
                  <input type="number" value={form.num_habitantes} onChange={e => set("num_habitantes", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934]" placeholder="Opcional" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Descripción del requerimiento</label>
                  <textarea rows={3} value={form.descripcion_libre} onChange={e => set("descripcion_libre", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934] resize-none" placeholder="Describe brevemente qué necesitas conseguir o resolver..." data-testid="input-descripcion" />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => { if (!form.solicitante_nombre || !form.titulo || !form.municipio) { setError("Completa los campos obligatorios (*)"); return; } setError(""); setStep(2); }}
                  className="bg-[#007934] text-white rounded-xl px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#006228] transition-colors"
                  data-testid="next-step-1"
                >
                  Siguiente <ChevronRight size={16} />
                </button>
              </div>
              {error && <p className="text-red-500 text-xs text-right">{error}</p>}
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div data-testid="public-step-2">
              <h2 className="font-bold text-gray-900 mb-4">Servicios requeridos <span className="text-[#007934] text-sm font-normal">({countSelected()} seleccionados)</span></h2>
              <p className="text-xs text-gray-400 mb-5">Marca todos los servicios que necesitas. No te preocupes si no estás seguro, el equipo te ayudará a concretarlos.</p>
              <CheckGroup title="Telecomunicaciones" options={TELCO_OPTS} state={form.servicios_telco} onChange={(f, v) => setServiceField("servicios_telco", f, v)} />
              <CheckGroup title="Software / Plataformas" options={SW_OPTS} state={form.servicios_software} onChange={(f, v) => setServiceField("servicios_software", f, v)} />
              <CheckGroup title="Hardware / IoT" options={HW_OPTS} state={form.servicios_hardware} onChange={(f, v) => setServiceField("servicios_hardware", f, v)} />
              <CheckGroup title="Integraciones" options={INTEG_OPTS} state={form.servicios_integraciones} onChange={(f, v) => setServiceField("servicios_integraciones", f, v)} />
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Otras integraciones / notas adicionales</label>
                <textarea rows={2} value={form.notas_adicionales} onChange={e => set("notas_adicionales", e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#007934] resize-none" placeholder="¿Hay alguna integración especial o comentario que debamos saber?" />
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="text-gray-500 text-sm flex items-center gap-1 hover:text-gray-700"><ChevronLeft size={15} /> Anterior</button>
                <button onClick={() => setStep(3)} className="bg-[#007934] text-white rounded-xl px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#006228] transition-colors" data-testid="next-step-2">
                  Revisar <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div data-testid="public-step-3">
              <h2 className="font-bold text-gray-900 mb-4">Resumen de la petición</h2>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-5 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Solicitante</span><span className="font-medium text-gray-900">{form.solicitante_nombre} {form.solicitante_cargo && `— ${form.solicitante_cargo}`}</span></div>
                {form.solicitante_email && <div className="flex justify-between"><span className="text-gray-400">Email</span><span className="text-gray-700">{form.solicitante_email}</span></div>}
                <div className="flex justify-between"><span className="text-gray-400">Título</span><span className="font-semibold text-gray-900">{form.titulo}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Municipio</span><span className="text-gray-700">{form.municipio}</span></div>
                {form.tipo_contrato && <div className="flex justify-between"><span className="text-gray-400">Servicio</span><span className="text-gray-700">{form.tipo_contrato}</span></div>}
                {form.plazo_cliente_meses && <div className="flex justify-between"><span className="text-gray-400">Plazo</span><span className="text-gray-700">{form.plazo_cliente_meses} meses</span></div>}
                {form.presupuesto_cliente && <div className="flex justify-between"><span className="text-gray-400">Presupuesto</span><span className="text-gray-700">{Number(form.presupuesto_cliente).toLocaleString("es-ES")} €</span></div>}
                {form.descripcion_libre && <div><p className="text-gray-400 mb-1">Descripción</p><p className="text-gray-700">{form.descripcion_libre}</p></div>}
                <div className="pt-1">
                  <p className="text-gray-400 mb-2">Servicios seleccionados ({countSelected()})</p>
                  <div className="flex flex-wrap gap-1">
                    {[...TELCO_OPTS.filter(o => form.servicios_telco[o.id]),
                      ...SW_OPTS.filter(o => form.servicios_software[o.id]),
                      ...HW_OPTS.filter(o => form.servicios_hardware[o.id]),
                      ...INTEG_OPTS.filter(o => form.servicios_integraciones[o.id])
                    ].map(o => (
                      <span key={o.id} className="text-xs bg-[#e8f5e9] text-[#007934] px-2.5 py-0.5 rounded-full font-medium">{o.label}</span>
                    ))}
                    {countSelected() === 0 && <span className="text-xs text-gray-400">Ninguno seleccionado</span>}
                  </div>
                </div>
              </div>
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
              <div className="bg-[#fffbeb] border border-amber-100 rounded-xl p-3 mb-5 text-xs text-amber-700">
                Al enviar, el equipo de Plataformas Corporativas recibirá tu petición y te contactará para una evaluación técnica y económica sin compromiso.
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep(2)} className="text-gray-500 text-sm flex items-center gap-1 hover:text-gray-700"><ChevronLeft size={15} /> Anterior</button>
                <button onClick={handleSubmit} disabled={submitting} className="bg-[#007934] text-white rounded-xl px-7 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-[#006228] transition-colors disabled:opacity-60" data-testid="submit-public-form">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Enviando...</> : <><Send size={15} /> Enviar petición</>}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Valoriza Plataformas Corporativas · <a href="mailto:plataformascorporativas@valorizasm.com" className="hover:text-[#007934] underline">plataformascorporativas@valorizasm.com</a>
        </p>
      </div>
    </div>
  );
}
